/**
 * Layout Selector Panel
 * Sprint 19: Custom Template Creation
 * 
 * Visual grid for selecting layout presets in template editor
 */

import React from 'react';
import { Tooltip } from 'antd';
import { CheckCircleFilled } from '@ant-design/icons';
import {
  landscapePresetsWithPreview,
  portraitPresetsWithPreview,
  squarePresetsWithPreview,
  LayoutPresetWithPreview
} from '../../presets/layoutPresetsPreview';
import './LayoutSelectorPanel.scss';

interface LayoutSelectorPanelProps {
  selectedPreset: string;
  onSelectPreset: (presetKey: string, presetAspectRatio: '16:9' | '9:16' | '1:1') => void;
  aspectRatio?: '16:9' | '9:16' | '1:1';
}

export const LayoutSelectorPanel: React.FC<LayoutSelectorPanelProps> = ({
  selectedPreset,
  onSelectPreset,
  aspectRatio = '16:9'
}) => {
  const renderPresetCard = (preset: LayoutPresetWithPreview) => {
    const isSelected = selectedPreset === preset.key;
    
    return (
      <Tooltip key={preset.key} title={preset.name}>
        <div
          className={`layout-preset-card ${isSelected ? 'selected' : ''}`}
          onClick={() => onSelectPreset(preset.key, aspectRatio)}
        >
          <div className="preset-preview">
            <img src={preset.preview} alt={preset.name} />
            {isSelected && (
              <div className="selected-indicator">
                <CheckCircleFilled />
              </div>
            )}
          </div>
          <div className="preset-name">{preset.name}</div>
          <div className="preset-requirements">
            {preset.requiresWebcam && <span className="req-webcam">Cam</span>}
            {preset.requiresScreen && <span className="req-screen">Screen</span>}
          </div>
        </div>
      </Tooltip>
    );
  };

  const getPresetsForRatio = () => {
    switch (aspectRatio) {
      case '16:9':
        return landscapePresetsWithPreview;
      case '9:16':
        return portraitPresetsWithPreview;
      case '1:1':
        return squarePresetsWithPreview;
      default:
        return landscapePresetsWithPreview;
    }
  };

  const getRatioLabel = () => {
    switch (aspectRatio) {
      case '16:9':
        return 'Landscape (16:9)';
      case '9:16':
        return 'Portrait (9:16)';
      case '1:1':
        return 'Square (1:1)';
      default:
        return 'Layout';
    }
  };

  const presets = getPresetsForRatio();
  const gridClassName = aspectRatio === '9:16' ? 'presets-grid portrait' : 
                        aspectRatio === '1:1' ? 'presets-grid square' : 'presets-grid';

  return (
    <div className="layout-selector-panel">
      <h4>Layout Preset ({getRatioLabel()})</h4>
      <div className={gridClassName}>
        {presets.map(p => renderPresetCard(p))}
      </div>
    </div>
  );
};

export default LayoutSelectorPanel;
